import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AccessRoleService } from '../../services';
import { handleNucleusResponse } from '../../services/utils';
import type {
  CreateAccessRoleRequest,
  UpdateAccessRoleRequest,
} from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';
import {
  deleteQueryItem,
  updateQueryItem,
  upsertQueryItem,
} from '@billx/utils';

export const useCurrentUserAccessRoles = () => {
  const queryClient = useQueryClient();

  const QUERY_KEY = ['currentUserAccessRoles'];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await AccessRoleService.fetchCurrentUserAccessRoles();

      return handleNucleusResponse(response).data;
    },
  });

  const createAccessRole = useMutation({
    mutationFn: (data: CreateAccessRoleRequest) =>
      AccessRoleService.createAccessRole(data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, upsertQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateAccessRole = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAccessRoleRequest }) =>
      AccessRoleService.updateAccessRole(id, data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const deleteAccessRole = useMutation({
    mutationFn: (id: string) => AccessRoleService.deleteAccessRole(id),
    onSuccess: (response, id) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, deleteQueryItem(id));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });
  const { data: accessRoles = [], ...restQuery } = query;
  return {
    accessRoles,
    ...restQuery,
    createAccessRole,
    updateAccessRole,
    deleteAccessRole,
  };
};
